/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useCallback, useRef, useState } from 'react';
import { DateRange, DayPicker, SelectRangeEventHandler } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { format } from 'date-fns/format';
import {
  getDateString,
  hasValidDates,
  offeringEndsAt,
  offeringStartsFrom,
  validateInRange,
} from '@camp67/model/booking/dates';
import { differenceInDays } from 'date-fns';
import { snackbarState } from '../snackbar-state';
import clsx from 'clsx';
import { isDisabled } from './date-field-single';
import { TentTypeV1, TentTypeV2 } from '@camp67/model';
import type { RoomsPerDay } from '../../state/reservation-store';

interface DateFieldProps {
  label: string;
  isRequired?: boolean;
  isMobile?: boolean;
  error?: string | null;
  onChange: (dateFrom?: Date, dateTo?: Date) => void;
  min: Date;
  max: Date;
  value: DateRange | undefined;
  tents: [TentTypeV1 | TentTypeV2, number][];
  availableDates: RoomsPerDay;
  bathingDates?: Date[];
  closeButton?: React.ReactNode;
}

const SNACKBAR_ERROR_ID = 'booking-date-field';

function validateOnlyStartDatePresent(from: Date) {
  return validateInRange(from);
}

function validateDateRangePresent(from: Date, to: Date) {
  const formattedStartDate = getDateString(from);
  const formattedEndDate = getDateString(to);

  return (
    validateInRange(from) &&
    validateInRange(to) &&
    hasValidDates(formattedStartDate, formattedEndDate)
  );
}

export const DateField: React.FC<DateFieldProps> = ({
  isRequired = false,
  isMobile = false,
  error,
  label,
  onChange,
  min,
  max,
  value,
  tents,
  availableDates,
  bathingDates = [],
  closeButton,
}) => {
  const [open, setOpen] = useState(false);

  const onOpen = useCallback(() => setOpen(true), []);
  const onClose = useCallback(() => setOpen(false), []);

  const hasError = snackbarState.message && snackbarState.id === SNACKBAR_ERROR_ID;

  const onEscape = useCallback((evt: React.KeyboardEvent) => {
    if (evt.code === 'Escape') {
      setOpen(false);
    }
  }, []);

  const onRangeReset = useCallback(() => {
    onChange(undefined, undefined);
  }, [onChange]);

  const onRangeChange: SelectRangeEventHandler = useCallback(
    (range) => {
      if (!range) {
        return;
      }

      const hasStartDate = Boolean(value?.from);
      const hasEndDate = Boolean(value?.to);

      // new selection has started
      if (hasStartDate && hasEndDate) {
        onRangeReset();
        return;
      }

      // if start is missing, reset
      if (!range.from) {
        onRangeReset();
        return;
      }

      if (!range.to && !validateOnlyStartDatePresent(range.from)) {
        return;
      }

      if (range.from && range.to && !validateDateRangePresent(range.from, range.to)) {
        snackbarState.setMessage({
          message: 'Invalid date range selected',
          id: SNACKBAR_ERROR_ID,
        });

        return;
      }

      onChange(range.from, range.to);

      // Close picker when a complete range is selected
      if (range.from && range.to) {
        onClose();
      }
    },
    [onChange, onClose, onRangeReset, value?.from, value?.to],
  );
  const dates = Object.keys(availableDates).map((date) => getDateString(new Date(date)));

  return (
    <div
      className="w-full flex flex-col gap-2 mt-5"
      data-container={label + `${isMobile ? '-mobile' : ''}`}
    >
      <div
        className="w-full py-3 px-5 flex flex-col bg-gray-100 rounded-md relative border-2 border-transparent focus-within:border-2 focus-within:border-blue-950"
        onKeyDown={onEscape}
      >
        {open ? (
          <DayPicker
            className={clsx(
              'absolute top-14 max-w-[95vw] left-1/2 -translate-x-1/2 sm:translate-x-0 sm:left-0 bg-white z-10 text-xs md:text-base rounded-md shadow-lg p-2 font-sans',
              {
                'border-2 border-transparent': !hasError,
                'border-2 border-red-200': hasError,
              },
            )}
            mode="range"
            fromDate={min}
            toDate={max}
            onSelect={onRangeChange}
            showOutsideDays
            fixedWeeks
            selected={value}
            disabled={(m) => isDisabled(m, tents, availableDates, value?.from)}
            disableNavigation={false}
            modifiers={{
              bathingDates,
              unavailableDates: (m) => isUnavailableCheckoutDate(m, dates, value?.from),
            }}
            modifiersStyles={{
              bathingDates: { background: 'orange' },
              unavailableDates: { background: 'red' },
            }}
            footer={<DateFieldFooter onClose={onClose} closeButton={closeButton} />}
          />
        ) : null}
        <label className="font-sans text-gray-250 font-regular text-sm">{label}*</label>
        <DisplayDateRange
          isRequired={isRequired}
          isMobile={isMobile}
          onOpen={onOpen}
          onClose={onClose}
          to={value?.to}
          label={label}
        />
      </div>
      {error ? <span className="text-red-200 pl-5">{error}</span> : null}
    </div>
  );
};

type DateFieldFooterProps = {
  onClose: VoidFunction;
  closeButton?: React.ReactNode;
};

function isUnavailableCheckoutDate(m: Date, dates: string[], fromDate?: Date): boolean {
  if (!fromDate) {
    return false;
  }

  if (differenceInDays(m, fromDate) < 2) {
    return false;
  }

  return (
    m >= offeringStartsFrom && m < offeringEndsAt && !dates.includes(getDateString(m))
  );
}

function DateFieldFooter({
  onClose,
  closeButton,
}: DateFieldFooterProps): React.ReactNode {
  return (
    <>
      {closeButton || (
        <button
          type="button"
          onClick={onClose}
          className="absolute -top-3 -right-3 md:-top-4 md:-right-4 bg-blue-100 rounded-full w-6 h-6 md:w-8 md:h-8 flex justify-center items-center"
        >
          ✕
        </button>
      )}
      <div className="flex flex-col">
        <span className="text-xs md:text-base">🟠 - bathing date</span>
        <span className="text-xs md:text-base">🔴 - fully booked</span>
      </div>
    </>
  );
}

type DisplayDateRangeProps = {
  isRequired: boolean;
  isMobile?: boolean;
  onOpen: VoidFunction;
  onClose: VoidFunction;
  to: Date | undefined;
  label: string;
};

const emptyDate = 'dd/mm/yyyy';

function DisplayDateRange({
  label,
  isRequired,
  onOpen,
  to,
  isMobile = false,
}: DisplayDateRangeProps) {
  const end = to ? format(to, 'dd/MM/yyyy') : emptyDate;

  const inputRef = useRef<HTMLInputElement>(null);

  const onBlur = useCallback(
    (evt: React.FocusEvent<HTMLInputElement, Element>): void => {
      const parentElement = document.querySelector(
        `[data-container="${label}${isMobile ? '-mobile' : ''}"]`,
      );

      if (parentElement?.contains(evt.relatedTarget as Node)) {
        evt.preventDefault();
        evt.stopPropagation();
        inputRef.current?.focus();
        return;
      }
    },
    [isMobile, label],
  );

  return (
    <input
      ref={inputRef}
      type="text"
      className="outline-none text-base md:text-base border-none w-full pt-1.5 m-0 bg-gray-100"
      placeholder="dd/mm/2025"
      required={isRequired}
      onClick={onOpen}
      onFocus={onOpen}
      onBlur={onBlur}
      onChange={() => {}}
      value={end}
      id={`end-date-field${isMobile ? '-mobile' : ''}`}
    />
  );
}
